<template>
  <template v-if="showCart()">
    <div
      v-for="(cartItem, index) in offerCartItems"
      class="choice-item"
      :class="{
        'choice-item--qty-editable':
          !isInOrderPage && isQuantityEditable(cartItem),
      }"
    >
      <div class="choice-item-details">
        <!-- FORGE-412 : emplacement image carre sur la carte selectionnee.
             En prod : rendu uniquement si une image est resolue (slot
             compactable, retombe sur l'ancien comportement). En preview
             (`globalConfig.previewMode=true`) : toujours rendu, avec un
             placeholder neutre quand pas d'image, pour materialiser le
             slot dans la galerie de scenarios. -->
        <div
          v-if="getImageSrc(cartItem) || showImagePlaceholder"
          class="choice-left-col"
          :class="{ 'choice-left-col--placeholder': !getImageSrc(cartItem) }"
        >
          <img v-if="getImageSrc(cartItem)" :src="getImageSrc(cartItem)" />
          <span v-else class="choice-image-placeholder" aria-hidden="true">
            <svg viewBox="0 0 48 48" width="100%" height="100%">
              <rect
                x="6"
                y="10"
                width="36"
                height="30"
                rx="3"
                ry="3"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              />
              <path
                d="M6 32 L18 22 L26 28 L36 16 L42 22 L42 40 L6 40 Z"
                fill="currentColor"
                opacity="0.18"
              />
              <circle
                cx="16"
                cy="18"
                r="2.5"
                fill="currentColor"
                opacity="0.5"
              />
            </svg>
          </span>
        </div>
        <div class="choice-right-col">
          <div class="title-price">
            <div>
              <div class="product-name">
                <!-- FORGE-412 : quand affiche dans une order page
                     (showingFrom=productListing), on n'interprete pas le
                     HTML : la carte selectionnee est compacte et la typo
                     du titre est portee par le CSS. Dans /cart standalone
                     (showingFrom=default), on garde le rendu HTML
                     historique. Cf. VanguardLabel.vue pour la rationale.

                     On rend le `displayChoice(cartItem)` (et non
                     `cartItem.choice`) pour qu'apres un toggle quick-sell,
                     la carte continue d'afficher l'offre parente : titre,
                     description, free-label et prix sont ceux de l'offre
                     "de base", l'upsell etant materialise par son propre
                     toggle UpsellList et reflete dans OrderSummary. -->
                <ChoiceVanguardLabel
                  :choice="displayChoice(cartItem)"
                  :plain-text="isInOrderPage"
                />
              </div>
              <span class="small-text"
                ><ChoiceVanguardDescription
                  :choice="displayChoice(cartItem)"
                  :plain-text="isInOrderPage"
              /></span>
              <div class="free-label">
                <ChoiceFreeLabel :choice="displayChoice(cartItem)" />
              </div>
            </div>
            <div class="small-text">
              <ChoicePrice :choice="displayChoice(cartItem)" />
            </div>
          </div>
          <div>
            <div class="quantity-frequency">
              <div
                v-show="!isInOrderPage && isQuantityEditable(cartItem)"
                class="btn btn-light qty-btn"
                :data-quantity-select="cartItem.choice.id"
              >
                <div class="qty-btn-text">
                  <label class="qty-btn-text-label">{{
                    $t("labels.qty")
                  }}</label>
                  <select
                    v-model="cartItem.qty"
                    v-on:input="validateQty(cartItem, $event)"
                    class="quantity-select"
                  >
                    <option
                      v-for="n in 15"
                      :value="n"
                      :selected="cartItem.qty === n"
                    >
                      {{ n }}
                    </option>
                  </select>
                  <span class="qty-btn-text-value">{{ cartItem.qty }}</span>
                </div>
              </div>
              <button
                v-show="!cart.hideDelete"
                type="button"
                name="deleteItem"
                @click.prevent="deleteItemAction(cartItem)"
                class="delete-item-btn"
                :title="$t('labels.deleteItem')"
              >
                <i class="fa-solid fa-trash-alt"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <CartTableUpsellList v-if="!noAlternativeChoice" :cartItem="cartItem" />
    </div>
  </template>
</template>

<script>
import CartTableUpsellList from "@/components/cart/table/UpsellList.vue";

import ChoiceFreeLabel from "@/components/choice/FreeLabel.vue";
import ChoiceVanguardDescription from "@/components/choice/VanguardDescription.vue";
import ChoiceVanguardLabel from "@/components/choice/VanguardLabel.vue";
import { useCartStore } from "@/store/cart";
import ChoicePrice from "@/components/choice/Price.vue";
import CartService from "@/services/cart";
import ChoiceImage from "@/services/choiceImage";
import OrderPageOffers from "@/services/orderPageOffers";
import {
  displayChoiceForOrderPage,
  isCartItemQuantityEditable,
  isOrderPageContext,
  validateCartItemQty,
} from "@/services/orderPageChoiceCatalog";

export default {
  name: "CartTableChoiceList",
  components: {
    ChoicePrice,
    ChoiceFreeLabel,
    ChoiceVanguardDescription,
    ChoiceVanguardLabel,
    CartTableUpsellList,
  },
  props: {
    showingFrom: {
      type: String,
      default: "default",
    },
  },
  data() {
    return {
      cart: null,
      lastCheckedCartSignature: null,
      productDetails: null,
    };
  },
  computed: {
    lastCartUpdate() {
      return useCartStore().lastUpdate;
    },
    // FORGE-412 — placeholder image visible UNIQUEMENT en preview, jamais
    // en prod : si une offre n'a pas d'image configuree cote Salesforce,
    // on ne rend pas de slot vide.
    showImagePlaceholder() {
      return window.globalConfig?.previewMode === true;
    },
    // FORGE-412 — vrai quand ce ChoiceList est rendu dans le contexte
    // d'une order page (carte selectionnee, montee via OrderPage.vue).
    // Conditionne le mode "texte plat" des composants enfants pour ne
    // pas interpreter le HTML des champs Salesforce. Dans /cart
    // standalone (showingFrom=default), on garde le rendu HTML historique.
    isInOrderPage() {
      return this.showingFrom === "productListing" || isOrderPageContext();
    },
    // Quand aucun choix alternatif n'est possible (offre unique ou
    // hideListing), le quick-sell est rendu par OrderSummary comme source
    // unique : on n'affiche donc pas le toggle ici pour eviter le doublon
    // (cas hideListing=true ou ChoiceList rend l'item recap a cote de
    // l'order-summary).
    noAlternativeChoice() {
      const hideListing =
        new URLSearchParams(window.location.search).get("hideListing") ===
        "true";
      return OrderPageOffers.hasNoAlternativeChoice({
        productDetails: this.productDetails || window.productDetails || {},
        hideListing,
      });
    },
    // Les lignes compagnon stack (vente flash) entrent dans le total / payload
    // mais ne doivent pas etre rendues comme une seconde carte d'offre.
    offerCartItems() {
      return (this.cart?.items || []).filter((item) => !item?.isQuickSellStack);
    },
  },
  watch: {
    lastCartUpdate: {
      handler(newValue, oldValue) {
        if (!window?.globalConfig?.domainId) {
          return;
        }
        this.cart = this.getCart();
      },
    },
    cart: {
      handler(newValue, oldValue) {
        if (!window?.globalConfig?.domainId) {
          return;
        }
        if (this.lastCheckedCartSignature !== useCartStore().calcSignature()) {
          this.lastCheckedCartSignature = useCartStore().calcSignature();
          this.$nextTick(() => {
            CartService.updateCart();
          });
        }
      },
      deep: true,
    },
  },
  mounted() {
    this.cart = this.getCart();
    this.productDetails = window.productDetails;
  },
  methods: {
    getCart() {
      return useCartStore().cart;
    },
    // FORGE-412 — Choice a utiliser pour le rendu informationnel d'une
    // carte selectionnee dans une order page (titre, description,
    // free-label, prix).
    //
    // Apres un toggle quick-sell, `UpsellList.toggleAction` mute
    // `cartItem.choice` vers le choice "upsell" pour que le cart pointe
    // vers la bonne variante (et que le total / OrderSummary soient
    // corrects). Cote carte parente cependant, le mental model est :
    //
    //   - La carte represente l'OFFRE (le parent, ce que l'utilisateur a
    //     choisi parmi plusieurs alternatives radio).
    //   - Le toggle quick-sell est une OPTION par-dessus l'offre, avec
    //     son propre libelle + prix delta (rendus par UpsellList).
    //   - OrderSummary reflete ce qui sera effectivement commande
    //     (offre + option = upsell choice).
    //
    // Sans cette indirection, le titre/description/prix de la carte
    // basculaient sur l'upsell apres toggle, donnant l'impression que
    // selectionner l'option changeait l'offre elle-meme (rapporte par
    // l'utilisateur : "le prix affiche sur l'offre change alors qu'il
    // ne devrait pas").
    //
    // Hors order page (showingFrom=default), on retombe sur le rendu
    // historique : la /cart standalone montre TOUS les items du panier
    // tels quels, sans notion d'"offre parente" a preserver.
    displayChoice(cartItem) {
      if (!this.isInOrderPage) {
        return cartItem.choice;
      }
      return displayChoiceForOrderPage(cartItem);
    },
    isQuantityEditable(cartItem) {
      return isCartItemQuantityEditable(cartItem, {
        orderPage: this.isInOrderPage,
      });
    },
    validateQty(item, event) {
      validateCartItemQty(item, event);
    },
    deleteItemAction(item) {
      CartService.deleteItem(item);
    },
    showCart() {
      if (this.showingFrom === "productListing") {
        return true;
      }

      const hideListing =
        new URLSearchParams(window.location.search).get("hideListing") ===
        "true";
      if (this.showingFrom === "default" && hideListing) {
        return true;
      }

      if (
        this.showingFrom === "default" &&
        this.productDetails &&
        !this.productDetails.limitCartTo1Item
      ) {
        return true;
      }

      return false;
    },
    getImageSrc(cartItem) {
      return (
        ChoiceImage.getChoiceImageSrc(cartItem, this.productDetails, {
          choice: cartItem.choice,
        }) || ""
      );
    },
  },
};
</script>

<style scoped>
/* FORGE-391: ecart entre la photo et le texte sur les order pages utilisant
   ChoiceList.vue (alternative a Image.vue/.product-item-photo). La regle
   .choice-item-details { gap: 16px } existe deja dans page1.css (template
   centrale, l. 643-648) mais la CSS marketing par order page (ex:
   redirecturl-bundle-2.css) override cette regle sans recopier le gap. Le
   scoped Vue ajoute [data-v-xxx] qui surclasse la regle marketing en
   specificite (0,2,0 vs 0,1,0) et restaure le gap. */
@media (min-width: 992px) {
  .choice-item-details {
    gap: 16px;
  }
}
</style>
